/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * TODO: GRAPHYTE-2511 wire up real behaviour for the "Copy" button (no
 * `onClick` handler yet) and source the MCP server URL from configuration
 * rather than the hardcoded constant below.
 */
import { cssMap, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import CopyIcon from '@atlaskit/icon/core/copy';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';

const MCP_SERVER_URL = 'https://mcp.atlassian.com/v1/mcp';

const cursorBlink = keyframes({
	'0%, 49%': {
		opacity: 1,
	},
	'50%, 100%': {
		opacity: 0,
	},
});

const styles = cssMap({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlockStart: '104px',
		paddingBlockEnd: token('space.0'),
		overflow: 'hidden',
	},
	window: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		maxWidth: '720px',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#292A2E',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '12px',
	},
	commandLine: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '40px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInlineEnd: '72px',
		margin: 0,
		font: token('font.body.large'),
		fontFamily: token('font.family.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '24px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.2px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#DDDEE1',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	prompt: {
		flexShrink: 0,
	},
	url: {
		flexShrink: 0,
	},
	cursor: {
		display: 'inline-block',
		width: '8px',
		height: '18px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#F9F9F9',
		animationName: cursorBlink,
		animationDuration: '1s',
		animationTimingFunction: 'steps(1, end)',
		animationIterationCount: 'infinite',
	},
	copyButton: {
		position: 'absolute',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		insetInlineEnd: '24px',
		insetBlockStart: '50%',
		transform: 'translateY(-50%)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		cursor: 'pointer',
		transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

export const McpTerminal = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.root}>
			<div css={styles.window}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlassian/i18n/no-literal-string-in-jsx */}
				<pre css={styles.commandLine}>
					<span css={styles.prompt}>{'>'}</span>
					<span css={styles.url}>{MCP_SERVER_URL}</span>
					<span css={styles.cursor} aria-hidden="true" />
				</pre>
				<button
					type="button"
					aria-label={formatMessage(messages.mcpTerminalCopyAriaLabel)}
					css={styles.copyButton}
				>
					<CopyIcon label="" color="currentColor" />
				</button>
			</div>
		</div>
	);
};
